import React, { useEffect, useState } from "react";
import axios from "axios";
import HeaderMain from "../components/header/HeaderMain";
import NavMain from "../components/nav/NavMain";
import { Col, Row, Form, FloatingLabel, Button, Modal } from "react-bootstrap";
import DatePicker from "react-datepicker";
import { format } from 'date-fns';
import "react-datepicker/dist/react-datepicker.css";
import GLOBALVAR from "../constants";
import LoadingSpinner from "../components/misc/LoadingSpinner";
import { useNavigate } from 'react-router-dom';  
import "../css/application-form.scss";

// Keep these utility functions outside the component
const normalizePhoneNumber = (value) => {
  if (!value) return value;
  
  // Remove all non-numeric characters except +
  let normalizedNumber = value.replace(/[^\d+]/g, '');
  
  // Handle different formats
  if (normalizedNumber.startsWith('+63')) {
    const digits = normalizedNumber.slice(3);
    return `+63${digits}`;
  }
  
  // Handle 0 prefix
  if (normalizedNumber.startsWith('0')) {
    const digits = normalizedNumber.slice(1);
    return `+63${digits}`;
  }
  
  // Handle numbers without prefix
  if (normalizedNumber.length > 0) {
    return `+63${normalizedNumber}`;
  }
  
  return normalizedNumber;
};

const isValidPhoneNumber = (number) => {
  if (!number) return false;
  const normalized = normalizePhoneNumber(number);
  return /^(\+63\d{10}|09\d{9})$/.test(normalized);
};

// Add this after the existing utility functions and before the ApplicationForm component
const validateForm = (data) => {
  const errors = [];
  
  // Required fields validation
  if (!data.lastName?.trim()) errors.push("Last name is required");
  if (!data.firstName?.trim()) errors.push("First name is required");
  if (!data.street?.trim()) errors.push("Street address is required");
  if (!data.barangay?.trim()) errors.push("Barangay is required");
  if (!data.municipality?.trim()) errors.push("Municipality/City is required");
  if (!data.province?.trim()) errors.push("Province is required");
  if (!data.zipCode?.trim()) errors.push("Zip code is required");
  if (!data.birthday) errors.push("Birthday is required");
  if (!data.gender || data.gender === "Select Gender") errors.push("Gender is required");
  if (!data.civilStatus || data.civilStatus === "Select Civil Status") errors.push("Civil Status is required");
  
  // Phone number validation
  if (!data.mobileNo1 || !isValidPhoneNumber(data.mobileNo1)) {
    errors.push("Mobile No. 1 must be in format: 09XXXXXXXXX or +639XXXXXXXXX");
  }
  
  if (data.mobileNo2 && !isValidPhoneNumber(data.mobileNo2)) {
    errors.push("Mobile No. 2 must be in format: 09XXXXXXXXX or +639XXXXXXXXX");
  }
  
  if (data.telephoneNo && !isValidPhoneNumber(data.telephoneNo)) {
    errors.push("Telephone No. must be in format: 09XXXXXXXXX or +639XXXXXXXXX");
  }
  
  // Email validation if provided
  if (data.emailAddress && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.emailAddress)) {
    errors.push("Invalid email address format");
  }
  
  // Married-specific validations
  if (data.civilStatus === "Married") {
    if (!data.marriedTo?.trim()) errors.push("Spouse name is required for married applicants");
  }
  
  return errors;
};

function ApplicationForm() {
    const [civilStatus, setCivilStatus] = useState("");
    const [isMarried, setIsMarried] = useState(false);
    const [birthday, setBirthday] = useState(null); // State to manage birthday
    const [showModal, setShowModal] = useState(false);
    const [modalHeader, setModalHeader] = useState(null);
    const [showSpinner, setShowSpinner] = useState(false);

    const handleClose = () => setShowModal(false);
    const handleShow = () => setShowModal(true);
    const [errMessages, setErrMessages] = useState(false);
    const navigate = useNavigate();

    const handleCivilStatusChange = (e) => {
        setCivilStatus(e.target.value);
        setIsMarried(e.target.value === "Married");
    };

    const handleBirthdayChange = (date) => {
        if (!(date instanceof Date) || isNaN(date)) {
            setBirthday(null);
            return;
        }
        
        setBirthday(date);
        const formattedDate = format(date, 'MM/dd/yyyy');
        handleChange({ target: { name: "birthday", value: formattedDate } });
    };

    const [formData, setFormData] = useState({
        lastName: '',
        firstName: '',
        middleName: '',
        mothersMaidenName: '',
        street: '',
        barangay: '',
        municipality: '',
        province: '',
        zipCode: '',
        birthday: '',
        gender: '',
        civilStatus: '',
        marriedTo: '',
        telephoneNo: '',
        mobileNo1: '',
        mobileNo2: '',
        emailAddress: '',
        sssOrGsis: '',
        passport: '',
        employerDetails: '',
        employerAddress: '',
        employerContactNumber: '',
        employerLocation: ''
    });

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
    };

    // Move phone-related handlers inside component
    const handlePhoneChange = (e) => {
        const { name, value } = e.target;
        const normalizedValue = normalizePhoneNumber(value);
        setFormData(prev => ({ ...prev, [name]: normalizedValue }));
    };

    const handlePhoneBlur = (e) => {
        const { name, value } = e.target;
        if (!value) return;

        const normalizedValue = normalizePhoneNumber(value);
        if (normalizedValue !== value) {
            setFormData(prev => ({ ...prev, [name]: normalizedValue }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setShowSpinner(true);
        setErrMessages([]);

        // Client-side validation
        const validationErrors = validateForm(formData);
        if (validationErrors.length > 0) {
            setModalHeader('Validation Error');
            setErrMessages(validationErrors);
            handleShow();
            setShowSpinner(false);
            return;
        }

        try {
            const response = await axios.post(`${GLOBALVAR['SERVER_ROOT']}/api/applications/add`, formData);
            console.log(response.data.message);
            navigate('/dashboard', { 
                state: { 
                    message: 'Application submitted successfully',
                    type: 'success'
                }
            });
        } catch (error) {
            console.error('Error submitting form:', error);
            setModalHeader('Submission Error');
            
            const errorsArray = error.response?.data?.error?.split(',\n') || ['An unexpected error occurred'];
            const filteredErrors = errorsArray.filter(error => error.trim() !== '');
            setErrMessages(filteredErrors);
            handleShow();
        } finally {
            setShowSpinner(false);
        }
    };

  useEffect(() => {
  // Any initialization logic here
  
  return () => {
    // Cleanup logic
    setFormData({});
    setBirthday(null);
    setCivilStatus("");
    setIsMarried(false);
  };
}, []);


  return (
    <>
      {showSpinner && (<LoadingSpinner />)}
      <NavMain slug="application-form" parent="1" />
      <HeaderMain slug="application-form" />
      <div className="main-content application-form">
        <Modal show={showModal} onHide={handleClose}>
          <Modal.Header closeButton>
            <Modal.Title className={errMessages ? 'text-danger' : 'text-success'}>
              {modalHeader}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {errMessages ? (
              <ul className="error-list">
                {errMessages.map((error, index) => (
                  <li key={index} className="text-danger">
                    {error.trim().replace('Validation error: ', '')}
                  </li>
                ))}
              </ul>
            ) : (
              <div className="text-center">
                <i className="fa-solid fa-circle-check text-success fs-1 mb-3"></i>
                <p>Application submitted successfully!</p>
              </div>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant={errMessages ? 'secondary' : 'primary'} onClick={handleClose}>
              {errMessages ? 'Close' : 'OK'}
            </Button>
          </Modal.Footer>
        </Modal>
        <Form onSubmit={handleSubmit}>
          <div className="form-section">
            <h2 className="section-title">
              <i className="fa-solid fa-user"></i>
              Personal Information
            </h2>
            <Row className="mb-3">
              <Col lg={4}>
                <FloatingLabel controlId="floatingLastName" label="Last Name">
                  <Form.Control type="text" name="lastName" placeholder="Last Name" onChange={handleChange} />
                </FloatingLabel>
              </Col>
              <Col lg={4}>
                <FloatingLabel controlId="floatingFirstName" label="First Name">
                  <Form.Control type="text" name="firstName" placeholder="First Name" onChange={handleChange} />
                </FloatingLabel>
              </Col>
              <Col lg={4}>
                <FloatingLabel controlId="floatingMiddleName" label="Middle Name">
                  <Form.Control type="text" name="middleName" placeholder="Middle Name" onChange={handleChange} />
                </FloatingLabel>
              </Col>
            </Row>
            <Row className="mb-3">
              <Col>
                <FloatingLabel controlId="floatingMothersMaidenName" label="Mother's Maiden Name">
                  <Form.Control type="text" name="mothersMaidenName" placeholder="Mother's Maiden Name" onChange={handleChange} />
                </FloatingLabel>
              </Col>
            </Row>
          </div>

          <div className="form-section">
            <h2 className="section-title">
              <i className="fa-solid fa-location-dot"></i>
              Address Information
            </h2>
            <Row className="mb-3">
              <Col md={6}>
                <FloatingLabel controlId="floatingStreet" label="House No. / Street">
                  <Form.Control type="text" name="street" placeholder="House No. / Street" onChange={handleChange} />
                </FloatingLabel>
              </Col>
              <Col md={6}>
                <FloatingLabel controlId="floatingBarangay" label="Barangay">
                  <Form.Control type="text" name="barangay" placeholder="Barangay" onChange={handleChange} />
                </FloatingLabel>
              </Col>
            </Row>
            <Row className="mb-3">
              <Col md={4}>
                <FloatingLabel controlId="floatingMunicipality" label="Municipality/City">
                  <Form.Control type="text" name="municipality" placeholder="Municipality/City" onChange={handleChange} />
                </FloatingLabel>
              </Col>
              <Col md={4}>
                <FloatingLabel controlId="floatingProvince" label="Province">
                  <Form.Control type="text" name="province" placeholder="Province" onChange={handleChange} />
                </FloatingLabel>
              </Col>
              <Col md={4}>
                <FloatingLabel controlId="floatingZipCode" label="Zip Code">
                  <Form.Control type="text" name="zipCode" placeholder="Zip Code" onChange={handleChange} />
                </FloatingLabel>
              </Col>
            </Row>
          </div>

          <div className="form-section">
            <h2 className="section-title">
              <i className="fa-solid fa-id-card"></i>
              Basic Details
            </h2>
            <Row className="mb-3">
              <Col md={4}>
                <div className="date-picker-wrapper">
                  <label>Birthday</label>
                  <DatePicker
                    className="form-control"
                    selected={birthday}
                    onChange={handleBirthdayChange}
                    dateFormat="MM/dd/yyyy"
                    peekNextMonth
                    showMonthDropdown
                    showYearDropdown
                    dropdownMode="select"
                    maxDate={new Date()}
                    name="birthday"
                    placeholderText="Select Birthday"
                  />
                </div>
              </Col>
              <Col md={4}>
                <FloatingLabel controlId="floatingGender" label="Gender">
                  <Form.Select defaultValue="Select Gender" name="gender" onChange={handleChange}>
                    <option>Select Gender</option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                  </Form.Select>
                </FloatingLabel>
              </Col>
              <Col md={4}>
                <FloatingLabel controlId="floatingCivilStatus" label="Civil Status">
                  <Form.Select value={civilStatus} name="civilStatus" onChange={(e)=>{handleCivilStatusChange(e);handleChange(e);}}>
                    <option>Select Civil Status</option>
                    <option value="Single">Single</option>
                    <option value="Married">Married</option>
                    <option value="Widow">Widow</option>
                    <option value="Separated">Separated</option>
                    <option value="Annulled">Annulled</option>
                  </Form.Select>
                </FloatingLabel>
              </Col>
            </Row>

            {isMarried && (
              <Row className="mb-3">
                <Col md={6}>
                  <FloatingLabel controlId="floatingMarriedTo" label="Married To">
                    <Form.Control type="text" placeholder="Married To" onChange={handleChange} name="marriedTo" />
                  </FloatingLabel>
                </Col>
                <Col md={6}>
                  <FloatingLabel controlId="floatingTelephoneNo" label="Telephone No.">
                    <Form.Control 
                      type="text" 
                      placeholder="Telephone No." 
                      onChange={handlePhoneChange} 
                      onBlur={handlePhoneBlur}
                      name="telephoneNo"
                      value={formData.telephoneNo || ''}
                      isValid={formData.telephoneNo && isValidPhoneNumber(formData.telephoneNo)}
                      isInvalid={formData.telephoneNo && !isValidPhoneNumber(formData.telephoneNo)}
                    />
                  </FloatingLabel>
                  <small className="text-muted">Format: 09XXXXXXXXX or +639XXXXXXXXX</small>
                </Col>
              </Row>
            )}
          </div>

          <div className="form-section">
            <h2 className="section-title">
              <i className="fa-solid fa-address-book"></i>
              Contact Information
            </h2>
            <Row className="mb-3">
              <Col md={6}>
                <FloatingLabel controlId="floatingMobileNo1" label="Mobile No. 1">
                  <Form.Control 
                    type="text"
                    placeholder="Mobile No. 1"
                    onChange={handlePhoneChange}
                    onBlur={handlePhoneBlur}
                    name="mobileNo1"
                    value={formData.mobileNo1 || ''}
                    isValid={formData.mobileNo1 && isValidPhoneNumber(formData.mobileNo1)}
                    isInvalid={formData.mobileNo1 && !isValidPhoneNumber(formData.mobileNo1)}
                  />
                </FloatingLabel>
                <Form.Text className={`phone-format ${formData.mobileNo1 && !isValidPhoneNumber(formData.mobileNo1) ? 'text-danger' : 'text-muted'}`}>
                  Format: 09XXXXXXXXX or +639XXXXXXXXX
                </Form.Text>
              </Col>
              <Col md={6}>
                <FloatingLabel controlId="floatingMobileNo2" label="Mobile No. 2">
                  <Form.Control 
                    type="text"
                    placeholder="Mobile No. 2"
                    onChange={handlePhoneChange}
                    onBlur={handlePhoneBlur}
                    name="mobileNo2"
                    value={formData.mobileNo2 || ''}
                    isValid={formData.mobileNo2 && isValidPhoneNumber(formData.mobileNo2)}
                    isInvalid={formData.mobileNo2 && !isValidPhoneNumber(formData.mobileNo2)}
                  />
                </FloatingLabel>
                <small className="text-muted">Format: 09XXXXXXXXX or +639XXXXXXXXX</small>
              </Col>
            </Row>
            <Row className="mb-3">
              <Col>
                <FloatingLabel controlId="floatingEmailAddress" label="E-Mail Address">
                  <Form.Control type="email" placeholder="E-Mail Address" onChange={handleChange} name="emailAddress" />
                </FloatingLabel>
              </Col>
            </Row>
          </div>

          <div className="form-section">
            <h2 className="section-title">
              <i className="fa-solid fa-building"></i>
              Employment Details
            </h2>
            <Row className="mb-3">
              <Col md={6}>
                <FloatingLabel controlId="floatingSSSOrGSIS" label="SSS/GSIS #">
                  <Form.Control type="text" placeholder="SSS/GSIS #" onChange={handleChange} name="sssOrGsis" />
                </FloatingLabel>
              </Col>
              <Col md={6}>
                <FloatingLabel controlId="floatingPassport" label="Passport #">
                  <Form.Control type="text" placeholder="Passport #" onChange={handleChange} name="passport" />
                </FloatingLabel>
              </Col>
            </Row>
            <Row className="mb-3">
              <Col md={6}>
                <FloatingLabel controlId="floatingEmployerDetails" label="Employer Name">
                  <Form.Control type="text" placeholder="Employer Name" onChange={handleChange} name="employerDetails" />
                </FloatingLabel>
              </Col>
              <Col md={6}>
                <FloatingLabel controlId="floatingEmployerAddress" label="Employer Address">
                  <Form.Control type="text" placeholder="Employer Address" onChange={handleChange} name="employerAddress" />
                </FloatingLabel>
              </Col>
            </Row>
            <Row className="mb-3">
              <Col md={6}>
                <FloatingLabel controlId="floatingEmployerContactNumber" label="Employer Contact Number">
                  <Form.Control type="text" placeholder="Employer Contact Number" onChange={handleChange} name="employerContactNumber" />
                </FloatingLabel>
              </Col>
              <Col md={6}>
                <FloatingLabel controlId="floatingEmployerLocation" label="Job Position">
                  <Form.Control type="text" placeholder="Job Position" onChange={handleChange} name="employerLocation" />
                </FloatingLabel>
              </Col>
            </Row>
          </div>

          <div className="form-actions">
            <Button variant="primary" type="submit" className="submit-btn" id="submit-application">
              <i className="fa-solid fa-paper-plane"></i>
              Submit Application
            </Button>
          </div>
        </Form>
      </div>
    </>
  );
}

export default ApplicationForm;
